import {
  useGetPayors,
  getGetPayorsQueryOptions,
} from "@/requests/gen/react-query/payor";
import {
  PayorRead,
  PayorTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { keepPreviousData } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import snakeToCamel from "@/lib/snake-to-camel";
import { Header } from "@/components/universal/header";
import parsePhoneNumber from "libphonenumber-js";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";

const payorSearchParsers = {
  payor: parseAsArrayOf(parseAsStringEnum(Object.values(PayorTypeEnum))),
};

const payorSearchAllParsers = {
  ...dataTableSearchParsers,
  ...payorSearchParsers,
};

const loadPayorSearch = createLoader(payorSearchAllParsers);

type PayorSearch = inferParserType<typeof payorSearchAllParsers>;

function payorQueryParams(
  p: PayorSearch,
): Parameters<typeof getGetPayorsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    payor_types: p.payor || [],
  };
}

export const Route = createFileRoute("/_private/_admin/payor/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetPayorsQueryOptions(
        payorQueryParams(loadPayorSearch(location.searchStr)),
      ),
    ),
  component: PayorLookup,
});

// Define the columns for the table
interface PayorTableProps {
  link?: LinkProps;
  name: string;
  email: string;
  phone: string;
  type: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<PayorTableProps>[] = [
  { header: "Name", accessorKey: "name" },
  { header: "Bin", accessorKey: "bin_number" },
  { header: "Email Address", accessorKey: "email" },
  { header: "Phone", accessorKey: "phone" },
  { header: "Type", accessorKey: "type" },
];

// Main component
function PayorLookup() {
  const [queryParams, setQueryParams] = useQueryStates(payorSearchAllParsers);

  const payors = useGetPayors(payorQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: PayorTableProps[] | undefined = payors.data?.items.map(
    (payor: PayorRead) => {
      return {
        link: linkOptions({
          to: "/payor/$payorId",
          params: { payorId: String(payor.payor_id) },
        }),
        name: payor.name,
        bin_number: payor.bin_number,
        email: payor.email || "",
        phone: parsePhoneNumber(payor.phone ?? "")?.formatNational() || "",
        type: payor.payor_type,
      };
    },
  );

  return (
    <div>
      <title>Payors - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Payors", link: { to: "/payor/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Payors</CardTitle>
          <CardDescription>
            View payors below. You can search, filter, and click to see more
            details.
          </CardDescription>
          <CardAction>
            <Link to="/payor/create">
              <Button variant="default" size="sm">
                <Plus />
                Create Payor
              </Button>
            </Link>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={payors.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(PayorTypeEnum).map((type) => ({
                    label: snakeToCamel(type),
                    value: type,
                  })),
                },
              ]}
              values={queryParams.payor || []}
              onChange={(values) => setQueryParams({ payor: values })}
            />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={payors.isLoading}
            isError={payors.isError}
            count={payors.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
